import { useState, useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar as CalendarComponent } from "@/components/ui/calendar";
import { ArrowLeft, Calendar, X } from "lucide-react";
import { format } from "date-fns";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useAlert } from "@/hooks/use-alert";
import { insertProjectSchema } from "@mangelapp/shared";
import { useTranslation } from 'react-i18next';
import { useDateFormat } from '@/utils/date-formatter';
import { ProjectImageUploader, ProjectImageUploaderRef } from "@/components/ProjectImageUploader";
import { LogoCropModal } from "@/components/LogoCropModal";
import { useOrganizationCurrency } from "@/hooks/use-organization-currency";
import { getSortedCountries, getCountryName } from '@/constants/countries';
import { useProjectStatuses } from '@/hooks/use-project-statuses';
import { useProjectPriorities } from '@/hooks/use-project-priorities';
import { useClientTypes } from '@/hooks/use-client-types';
import { projectDateToLocalDate } from '@/utils/project-dates';
import { CornerLabelField } from "@/components/ui/corner-label-field";
import { IconButtonCancel, IconButtonSave } from "@/components/ui/icon-buttons";
import {
  projectFormFieldStyle,
  projectFormInputClassName,
  projectFormSelectTriggerClassName,
} from "@/components/project/ProjectFormCardLayout";

interface NewProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const createFormSchema = (t: (key: string) => string) => z.object({
  projectNumber: z.string().min(1, t('components.projectModal:errors.projectNumberRequired')),
  name: z.string().min(1, t('components.projectModal:errors.nameRequired')),
  street: z.string().optional(),
  houseNo: z.string().optional(),
  addressSupplement: z.string().optional(),
  city: z.string().min(1, t('components.projectModal:errors.cityRequired')),
  postalCode: z.string().optional(),
  country: z.string().min(1, t('components.projectModal:errors.countryRequired')),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  auftraggeber: z.string().optional(),
  clientTypeId: z.string().optional(),
  managerId: z.string().optional(),
  managerName: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
  fortschritt: z.number().optional(),
  currency: z.string().optional(),
  projectPriorityId: z.string().min(1, t('components.projectModal:errors.priorityRequired')),
  projectStatusId: z.string().optional(),
  bildUrl: z.string().optional(),
  projektLogo: z.string().optional(),
});

type FormData = z.infer<ReturnType<typeof createFormSchema>>;

type OrgMember = { user_id: string; role: string; status: string; email: string; display_name: string | null };

export default function NewProjectModal({ isOpen, onClose }: NewProjectModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t, i18n } = useTranslation(['components.projectModal', 'pages.projects', 'pages.projectDetails', 'common']);
  const { formatDate } = useDateFormat();
  const orgCurrency = useOrganizationCurrency();
  
  // Fetch project statuses and priorities from database (only when modal is open)
  const { data: projectStatuses = [] } = useProjectStatuses();
  const { data: projectPriorities = [] } = useProjectPriorities();
  const { data: clientTypes = [] } = useClientTypes();
  
  const formSchema = createFormSchema(t);
  
  // Get default IDs when data is loaded (use semanticType for language-agnostic match)
  const defaultStatusId = projectStatuses.find(s => (s.semanticType || '').toLowerCase() === 'active')?.id || projectStatuses[0]?.id || '';
  const defaultPriorityId = projectPriorities.find(p => p.name === 'Middle')?.id || projectPriorities[0]?.id || '';
  
  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      projectNumber: "",
      name: "",
      street: "",
      houseNo: "",
      addressSupplement: "",
      city: "",
      postalCode: "",
      country: "",
      latitude: null,
      longitude: null,
      auftraggeber: "",
      clientTypeId: "",
      managerId: "",
      managerName: "",
      startDate: "",
      endDate: "",
      fortschritt: 0,
      currency: orgCurrency || "EUR",
      projectPriorityId: defaultPriorityId,
      projectStatusId: defaultStatusId,
    },
    mode: "onSubmit",
    reValidateMode: "onBlur",
  });
  
  // Update form defaults when data loads
  useEffect(() => {
    if (defaultStatusId && !form.getValues('projectStatusId')) {
      form.setValue('projectStatusId', defaultStatusId);
    }
    if (defaultPriorityId && !form.getValues('projectPriorityId')) {
      form.setValue('projectPriorityId', defaultPriorityId);
    }
  }, [defaultStatusId, defaultPriorityId, form]);

  const [coordinateWarning, setCoordinateWarning] = useState<string | undefined>();
  const [weatherValidationError, setWeatherValidationError] = useState<string | null>(null);
  const [isValidatingWeather, setIsValidatingWeather] = useState(false);
  const imageUploaderRef = useRef<ProjectImageUploaderRef>(null);
  const { alert } = useAlert();
  
  const [showLogoCropModal, setShowLogoCropModal] = useState(false);
  const [selectedLogoFile, setSelectedLogoFile] = useState<File | null>(null);
  const [pendingLogoFile, setPendingLogoFile] = useState<File | null>(null);
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null);
  const logoInputRef = useRef<HTMLInputElement>(null);

  const parseApiError = (error: any): { message: string; code?: string } => {
    let message = error?.message || t('components.projectModal:errors.generic');
    let code = error?.code as string | undefined;

    const rawResponse = error?.responseText;
    if (typeof rawResponse === 'string' && rawResponse.length > 0) {
      try {
        const parsed = JSON.parse(rawResponse);
        if (typeof parsed?.message === 'string' && parsed.message.length > 0) {
          message = parsed.message;
        }
        if (typeof parsed?.error === 'string' && parsed.error.length > 0) {
          code = parsed.error;
        } else if (typeof parsed?.code === 'string' && parsed.code.length > 0) {
          code = parsed.code;
        }
      } catch {
        // Keep existing fallback message/code values.
      }
    }

    return { message, code };
  };

  // Load organization and members for manager options (only when modal is open)
  const { data: orgMeResponse } = useQuery({
    queryKey: ["/api/orgs/me"],
    queryFn: async () => {
      const res = await apiRequest("/api/orgs/me");
      return await res.json();
    },
    enabled: isOpen,
    staleTime: 60_000,
    retry: false,
  });
  const orgInfo = (orgMeResponse as any)?.data || null;
  const orgId = orgInfo?.id || null;

  const { data: membersResponse } = useQuery({
    queryKey: ["/api/orgs", orgId, "members"],
    queryFn: async () => {
      const res = await apiRequest(`/api/orgs/${orgId}/members`);
      return await res.json();
    },
    enabled: isOpen && !!orgId,
    staleTime: 60_000,
    retry: false,
  });
  const members: OrgMember[] = ((membersResponse as any)?.data || []) as OrgMember[];
  const managerOptions = members
    .filter(m => m.status === 'active')
    .map(m => ({ id: m.user_id, name: m.display_name || m.email }));

  const getClientTypeDisplayName = (clientTypeName: string): string => {
    const clientTypeMap: Record<string, string> = {
      Bay: t('pages.projects:clientType.bay'),
      Bayan: t('pages.projects:clientType.bayan'),
      Firma: t('pages.projects:clientType.firma'),
    };
    return clientTypeMap[clientTypeName] || clientTypeName;
  };

  const handleLogoFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!file.type.startsWith('image/')) {
      await alert(t('pages.projectDetails:settings.logos.invalidFileType'));
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      await alert(t('pages.projectDetails:settings.logos.fileTooLarge'));
      return;
    }

    setSelectedLogoFile(file);
    setShowLogoCropModal(true);
  };

  const handleLogoCropSave = (croppedFile: File) => {
    setShowLogoCropModal(false);
    setSelectedLogoFile(null);
    
    const objectUrl = URL.createObjectURL(croppedFile);
    if (logoPreviewUrl) {
      URL.revokeObjectURL(logoPreviewUrl);
    }
    setLogoPreviewUrl(objectUrl);
    setPendingLogoFile(croppedFile);
  };

  const handleLogoCropCancel = () => {
    setShowLogoCropModal(false);
    setSelectedLogoFile(null);
  };

  const handleRemoveLogo = () => {
    if (logoPreviewUrl) {
      URL.revokeObjectURL(logoPreviewUrl);
    }
    setLogoPreviewUrl(null);
    setPendingLogoFile(null);
  };

  const uploadPendingLogo = async (projectId: string): Promise<string | null> => {
    if (!pendingLogoFile) return null;

    try {
      const formData = new FormData();
      formData.append('logo', pendingLogoFile);
      formData.append('projectId', projectId);

      const uploadResponse = await apiRequest('/api/uploads/project-logo', {
        method: 'POST',
        body: formData,
      });

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload logo');
      }

      const uploadResult = await uploadResponse.json();
      if (uploadResult.success && uploadResult.data?.url) {
        await apiRequest(`/api/projects/${projectId}`, {
          method: 'PUT',
          body: JSON.stringify({ projektLogo: uploadResult.data.url }),
        });
        return uploadResult.data.url;
      }
      return null;
    } catch (error) {
      console.error('Error uploading project logo:', error);
      return null;
    }
  };

  const createProjectMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const selectedProjectStatus = projectStatuses.find(s => s.id === (data.projectStatusId || defaultStatusId));
      const projectData: any = {
        projektNummer: data.projectNumber,
        name: data.name,
        street: data.street || undefined,
        houseNo: data.houseNo || undefined,
        addressSupplement: data.addressSupplement || undefined,
        city: data.city || undefined,
        postalCode: data.postalCode || undefined,
        country: data.country || undefined,
        latitude: data.latitude ?? undefined,
        longitude: data.longitude ?? undefined,
        auftraggeber: data.auftraggeber || undefined,
        clientTypeId: data.clientTypeId || undefined,
        managerId: data.managerId || undefined,
        managerName: data.managerName || undefined,
        startDatum: data.startDate || undefined,
        endDatum: data.endDate || undefined,
        fortschritt: data.fortschritt ?? 0,
        currency: data.currency || undefined,
      projectPriorityId: data.projectPriorityId,
      projectStatusId: data.projectStatusId || defaultStatusId,
      // Legacy fields for backward compatibility during migration
      prioritaet: projectPriorities.find(p => p.id === data.projectPriorityId)?.name || 'Middle',
      status: selectedProjectStatus?.displayName || selectedProjectStatus?.name || 'Active',
        bildUrl: data.bildUrl || undefined,
      };
      
      try {
        const { apiRequest } = await import("@/lib/queryClient");
        const response = await apiRequest("/api/projects", {
          method: "POST",
          body: JSON.stringify(projectData)
        });
        
        const result = await response.json();
        
        // Show warning if coordinates don't match address
        if (result.warning) {
          setCoordinateWarning(result.warning);
        }
        
        return result;
      } catch (error: any) {
        // Handle 403 and other HTTP errors specifically
        if (error.message && error.message.includes('403:')) {
          try {
            // Try to parse the error response to get the specific message
            const errorText = error.message.split(': ')[1];
            const errorData = JSON.parse(errorText);
            throw new Error(errorData.message || 'Project limit reached');
          } catch (parseError) {
            // If parsing fails, use the original error message
            throw new Error('Project limit reached. Please upgrade your subscription.');
          }
        }
        throw error;
      }
    },
    onSuccess: async (result: any) => {
      const created = result?.data || result;
      const newProjectId = created?.id;
      
      if (newProjectId && imageUploaderRef.current?.hasPendingFile()) {
        await imageUploaderRef.current.uploadPendingFile(newProjectId);
      }
      
      if (newProjectId && pendingLogoFile) {
        await uploadPendingLogo(newProjectId);
        if (logoPreviewUrl) {
          URL.revokeObjectURL(logoPreviewUrl);
        }
        setLogoPreviewUrl(null);
        setPendingLogoFile(null);
      }
      
      onClose();
      form.reset();
      
      await queryClient.invalidateQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key.length > 0 && 
                 (key[0] === "/api/projects" || 
                  (typeof key[0] === 'string' && key[0].startsWith("/api/projects")));
        },
        refetchType: 'all'
      });
      
      await queryClient.refetchQueries({ 
        predicate: (query) => {
          const key = query.queryKey;
          return Array.isArray(key) && key.length > 0 && 
                 (key[0] === "/api/projects" || 
                  (typeof key[0] === 'string' && key[0].startsWith("/api/projects")));
        },
        type: 'all'
      });
      
      queryClient.invalidateQueries({ queryKey: ["/api/stats"] });
    },
    onError: (error: any) => {
      console.error('❌ Project creation error:', error);
      const parsedError = parseApiError(error);
      
      // Check if it's a weather validation error
      const isWeatherValidationError = (
        parsedError.code === 'WEATHER_VALIDATION_FAILED' ||
        parsedError.message.includes('Weather data cannot be retrieved') ||
        parsedError.message.includes('Wetterdaten können nicht abgerufen werden') ||
        parsedError.message.includes('Hava durumu verileri alınamıyor')
      );
      
      // Check if it's a project limit error
      const isProjectLimitError = (
        parsedError.message.includes('Project limit reached') ||
        parsedError.message.includes('Projektlimit erreicht') ||
        parsedError.message.includes('Proje limiti aşıldı')
      );
      
      if (isWeatherValidationError) {
        setWeatherValidationError(parsedError.message || t('components.projectModal:errors.weatherValidationFailed'));
      }
      
      toast({
        title: isProjectLimitError 
          ? t('components.projectModal:errors.upgradeRequired')
          : isWeatherValidationError
          ? t('components.projectModal:errors.weatherValidationFailed')
          : t('components.projectModal:errors.generic'),
        description: isProjectLimitError 
          ? t('components.projectModal:errors.projectLimitReached')
          : (parsedError.message || t('components.projectModal:errors.generic')),
        variant: "destructive",
      });
    },
  });

  // Validate weather data can be retrieved
  const validateWeatherData = async (formData: FormData): Promise<boolean> => {
    setIsValidatingWeather(true);
    setWeatherValidationError(null);
    
    try {
      const response = await apiRequest('/api/weather', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(
          formData.latitude !== null &&
          formData.latitude !== undefined &&
          formData.longitude !== null &&
          formData.longitude !== undefined
            ? {
                latitude: formData.latitude,
                longitude: formData.longitude,
              }
            : {
                street: formData.street || undefined,
                houseNo: formData.houseNo || undefined,
                city: formData.city || undefined,
                postalCode: formData.postalCode || undefined,
                country: formData.country || undefined,
              }
        ),
      });
      
      const data = await response.json();
      if (!data.success) {
        setWeatherValidationError(data.message || t('components.projectModal:errors.weatherValidationFailed'));
        return false;
      }
      return true;
    } catch (error: any) {
      const parsedError = parseApiError(error);
      const errorMessage = parsedError.message || t('components.projectModal:errors.weatherValidationFailed');
      setWeatherValidationError(errorMessage);
      return false;
    } finally {
      setIsValidatingWeather(false);
    }
  };

  const onSubmit = async (data: FormData) => {
    // Validation is handled by react-hook-form before this is called
    const selectedManager = managerOptions.find(m => m.id === data.managerId);
    if (selectedManager) {
      data.managerName = selectedManager.name;
    } else if (data.managerId) {
      // If managerId exists but no manager found, clear it
      data.managerId = "";
      data.managerName = "";
    }
    
    // Validate weather data before submitting
    const isValid = await validateWeatherData(data);
    if (!isValid) {
      // Error is already set in validateWeatherData
      return;
    }
    
    createProjectMutation.mutate(data);
  };

  // Validate via zod safeParse and map issues to RHF errors
  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const values = form.getValues();
    const result = formSchema.safeParse(values);
    if (!result.success) {
      // Clear previous errors then set new ones
      form.clearErrors();
      for (const issue of result.error.issues) {
        const field = issue.path?.[0];
        if (field) {
          form.setError(field as any, {
            type: 'validation',
            message: issue.message,
          }, { shouldFocus: false });
        }
      }
      return;
    }
    onSubmit(result.data);
  };

  const handleClose = () => {
    form.reset();
    setCoordinateWarning(undefined);
    setWeatherValidationError(null);
    setIsValidatingWeather(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent 
        className="ticket-modal-shell [&>button]:hidden"
        tabIndex={-1}
        hideClose
      >
        <div className="ticket-modal-header">
          <div className="ticket-modal-title-row">
            <Button
              variant="ghost"
              size="sm"
              type="button"
              onClick={handleClose}
              className="h-8 w-8 p-0 text-[#333333] hover:text-[#333333] hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <DialogTitle className="ticket-modal-title">{t('components.projectModal:title')}</DialogTitle>
          </div>
          <div className="ticket-modal-actions">
            <IconButtonCancel
              onClick={handleClose}
              size={30}
              title={t('components.projectModal:actions.cancel')}
            />
            <IconButtonSave
              onClick={() => {
                const formEl = document.getElementById('new-project-form') as HTMLFormElement | null;
                formEl?.requestSubmit();
              }}
              disabled={createProjectMutation.isPending || isValidatingWeather || !!weatherValidationError}
              size={30}
              title={
                createProjectMutation.isPending || isValidatingWeather
                  ? t('components.projectModal:actions.creating')
                  : t('components.projectModal:actions.create')
              }
            />
          </div>
        </div>
        <DialogDescription className="sr-only">
          {t('components.projectModal:title')}
        </DialogDescription>
        <div className="ticket-modal-scroll">
          <div className="p-4 pt-2 -mt-[8px]">
            <Form {...form}>
              <form id="new-project-form" onSubmit={handleFormSubmit} className="space-y-0">
                <div className="max-w-[1112px]">
                  <div className="card-container card-container-muted card-container-no-shadow p-[32px]">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
            {/* Project Number */}
            <FormField
              control={form.control}
              name="projectNumber"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <CornerLabelField label={`${t('components.projectModal:fields.projectNumber')} *`}>
                    <FormControl>
                      <Input
                        placeholder={t('components.projectModal:fields.projectNumberPlaceholder') as string}
                        className={projectFormInputClassName}
                        style={projectFormFieldStyle}
                        {...field}
                      />
                    </FormControl>
                  </CornerLabelField>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            {/* Project Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <CornerLabelField label={`${t('components.projectModal:fields.name')} *`}>
                    <FormControl>
                      <Input
                        placeholder={t('components.projectModal:fields.namePlaceholder') as string}
                        className={projectFormInputClassName}
                        style={projectFormFieldStyle}
                        {...field}
                      />
                    </FormControl>
                  </CornerLabelField>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            {/* Project Location - Structured Address Fields */}
            <div className="space-y-3">
              {/* Street + House No */}
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="street"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <CornerLabelField label={t('components.projectModal:fields.street')}>
                        <FormControl>
                          <Input
                            placeholder={t('components.projectModal:fields.streetPlaceholder') as string}
                            className={projectFormInputClassName}
                            style={projectFormFieldStyle}
                            {...field}
                            onChange={(event) => {
                              setWeatherValidationError(null);
                              field.onChange(event);
                            }}
                          />
                        </FormControl>
                      </CornerLabelField>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="houseNo"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <CornerLabelField label={t('components.projectModal:fields.houseNo')}>
                        <FormControl>
                          <Input
                            placeholder={t('components.projectModal:fields.houseNoPlaceholder') as string}
                            className={projectFormInputClassName}
                            style={projectFormFieldStyle}
                            {...field}
                            onChange={(event) => {
                              setWeatherValidationError(null);
                              field.onChange(event);
                            }}
                          />
                        </FormControl>
                      </CornerLabelField>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>

              {/* Address Supplement single row */}
              <FormField
                control={form.control}
                name="addressSupplement"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <CornerLabelField label={t('components.projectModal:fields.addressSupplement')}>
                      <FormControl>
                        <Input
                          placeholder={t('components.projectModal:fields.addressSupplementPlaceholder') as string}
                          className={projectFormInputClassName}
                          style={projectFormFieldStyle}
                          {...field}
                        />
                      </FormControl>
                    </CornerLabelField>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />

              {/* ZIP + City */}
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="postalCode"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <CornerLabelField label={t('components.projectModal:fields.postalCode')}>
                        <FormControl>
                          <Input
                            placeholder={t('components.projectModal:fields.postalCodePlaceholder') as string}
                            className={projectFormInputClassName}
                            style={projectFormFieldStyle}
                            {...field}
                            onChange={(event) => {
                              setWeatherValidationError(null);
                              field.onChange(event);
                            }}
                            onBlur={async () => {
                              field.onBlur();
                              const formData = form.getValues();
                              if (formData.city || formData.postalCode || formData.country) {
                                await validateWeatherData(formData);
                              }
                            }}
                          />
                        </FormControl>
                      </CornerLabelField>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="city"
                  render={({ field }) => (
                    <FormItem className="space-y-1">
                      <CornerLabelField label={`${t('components.projectModal:fields.city')} *`}>
                        <FormControl>
                          <Input
                            placeholder={t('components.projectModal:fields.cityPlaceholder') as string}
                            className={projectFormInputClassName}
                            style={projectFormFieldStyle}
                            {...field}
                            onChange={(event) => {
                              setWeatherValidationError(null);
                              field.onChange(event);
                            }}
                            onBlur={async () => {
                              field.onBlur();
                              const formData = form.getValues();
                              if (formData.city || formData.postalCode || formData.country) {
                                await validateWeatherData(formData);
                              }
                            }}
                          />
                        </FormControl>
                      </CornerLabelField>
                      <FormMessage className="text-xs" />
                    </FormItem>
                  )}
                />
              </div>

              {/* Country single row */}
              <FormField
                control={form.control}
                name="country"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <CornerLabelField label={`${t('components.projectModal:fields.country')} *`}>
                      <FormControl>
                        <Select
                          value={field.value || ''}
                          onValueChange={async (value) => {
                            setWeatherValidationError(null);
                            field.onChange(value);
                            const formData = form.getValues();
                            if (formData.city || formData.postalCode || value) {
                              await validateWeatherData({ ...formData, country: value });
                            }
                          }}
                        >
                          <SelectTrigger className={projectFormSelectTriggerClassName} style={projectFormFieldStyle}>
                            <SelectValue placeholder={t('components.projectModal:fields.countryPlaceholder') as string}>
                              {field.value ? getCountryName(field.value, i18n.language as 'en' | 'de' | 'tr') : ''}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {getSortedCountries(i18n.language as 'en' | 'de' | 'tr').map((country) => (
                              <SelectItem key={country.code} value={country.code} className="text-xs">
                                {country[i18n.language as 'en' | 'de' | 'tr']}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </FormControl>
                    </CornerLabelField>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />

              {/* Weather Validation Error */}
              {weatherValidationError && (
                <div className="mt-2 p-2 rounded-lg border border-red-200 bg-red-50">
                  <p className="text-xs text-red-800">{weatherValidationError}</p>
                  {isValidatingWeather && (
                    <p className="text-xs text-red-600 mt-1">{t('components.projectModal:validating')}</p>
                  )}
                </div>
              )}

              {/* Optional Manual Coordinates - Hidden for now, may be needed in future */}
              <details className="mt-2 hidden">
                <summary className="text-xs text-muted cursor-pointer">{t('components.projectModal:fields.coordinatesOptional')}</summary>
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <FormField
                    control={form.control}
                    name="latitude"
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <CornerLabelField label={t('components.projectModal:fields.latitude')}>
                          <FormControl>
                            <Input
                              type="number"
                              step="any"
                              placeholder="52.5200"
                              {...field}
                              value={field.value ?? ''}
                              onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                            />
                          </FormControl>
                        </CornerLabelField>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                  
                  <FormField
                    control={form.control}
                    name="longitude"
                    render={({ field }) => (
                      <FormItem className="space-y-1">
                        <CornerLabelField label={t('components.projectModal:fields.longitude')}>
                          <FormControl>
                            <Input
                              type="number"
                              step="any"
                              placeholder="13.4050"
                              {...field}
                              value={field.value ?? ''}
                              onChange={(e) => field.onChange(e.target.value ? parseFloat(e.target.value) : null)}
                            />
                          </FormControl>
                        </CornerLabelField>
                        <FormMessage className="text-xs" />
                      </FormItem>
                    )}
                  />
                </div>
              </details>

              {coordinateWarning && (
                <div className="text-xs text-yellow-600 bg-yellow-50 p-2 rounded">
                  {coordinateWarning}
                </div>
              )}
            </div>

            <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="clientTypeId"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <CornerLabelField label={t('pages.projectDetails:form.clientType')}>
                      <Select value={field.value || ''} onValueChange={field.onChange}>
                        <FormControl>
                          <SelectTrigger className={projectFormSelectTriggerClassName} style={projectFormFieldStyle}>
                            <SelectValue placeholder={t('pages.projectDetails:form.clientTypePlaceholder') as string} />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {clientTypes.map((clientType) => (
                            <SelectItem key={clientType.id} value={clientType.id} className="text-xs">
                              {getClientTypeDisplayName(clientType.name)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </CornerLabelField>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="auftraggeber"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <CornerLabelField label={t('pages.projectDetails:form.client')}>
                      <FormControl>
                        <Input
                          placeholder={t('pages.projectDetails:form.clientPlaceholder') as string}
                          className={projectFormInputClassName}
                          style={projectFormFieldStyle}
                          {...field}
                        />
                      </FormControl>
                    </CornerLabelField>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="currency"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <CornerLabelField label={t('components.projectModal:fields.currency')}>
                    <Select value={field.value} onValueChange={field.onChange}>
                      <FormControl>
                        <SelectTrigger className={projectFormSelectTriggerClassName} style={projectFormFieldStyle}>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="EUR" className="text-xs">EUR (€)</SelectItem>
                        <SelectItem value="USD" className="text-xs">USD ($)</SelectItem>
                        <SelectItem value="GBP" className="text-xs">GBP (£)</SelectItem>
                        <SelectItem value="CHF" className="text-xs">CHF (CHF)</SelectItem>
                        <SelectItem value="TRY" className="text-xs">TRY (₺)</SelectItem>
                        <SelectItem value="PLN" className="text-xs">PLN (zł)</SelectItem>
                        <SelectItem value="DKK" className="text-xs">DKK (kr)</SelectItem>
                        <SelectItem value="SEK" className="text-xs">SEK (kr)</SelectItem>
                        <SelectItem value="NOK" className="text-xs">NOK (kr)</SelectItem>
                        <SelectItem value="CZK" className="text-xs">CZK (Kč)</SelectItem>
                        <SelectItem value="HUF" className="text-xs">HUF (Ft)</SelectItem>
                        <SelectItem value="RON" className="text-xs">RON (lei)</SelectItem>
                        <SelectItem value="BGN" className="text-xs">BGN (лв)</SelectItem>
                      </SelectContent>
                    </Select>
                  </CornerLabelField>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="managerId"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <CornerLabelField label={t('pages.projectDetails:form.projectManager')}>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger className={projectFormSelectTriggerClassName} style={projectFormFieldStyle}>
                          <SelectValue placeholder={t('pages.projectDetails:form.selectManager') as string} />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {(managerOptions || []).map((manager) => (
                          <SelectItem key={manager.id} value={manager.id} className="text-xs">
                            {manager.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </CornerLabelField>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="projectStatusId"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <CornerLabelField label={t('pages.projectDetails:form.status')}>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value || defaultStatusId}
                      value={field.value || defaultStatusId}
                    >
                      <FormControl>
                        <SelectTrigger className={projectFormSelectTriggerClassName} style={projectFormFieldStyle}>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {projectStatuses.map((status) => (
                          <SelectItem key={status.id} value={status.id} className="text-xs">
                            {(() => {
                              const semantic = String(status.semanticType || '').toLowerCase().replace('-', '_');
                              if (semantic === 'active') return t('pages.projects:status.active');
                              if (semantic === 'paused') return t('pages.projects:status.paused');
                              if (semantic === 'completed') return t('pages.projects:status.completed');
                              return status.displayName || status.name;
                            })()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </CornerLabelField>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />

            {/* Start and End Date */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="startDate"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <CornerLabelField label={t('pages.projectDetails:form.startDate')}>
                      <FormControl>
                        <Popover>
                          <div className="textbox-base bg-white flex h-9 min-h-9 items-center gap-1">
                            <PopoverTrigger asChild>
                              <button type="button" className="flex w-full items-center justify-end text-right">
                                {field.value ? (
                                  <span className="text-slate-900">{formatDate(field.value)}</span>
                                ) : (
                                  <span className="text-slate-400">{t('pages.projectDetails:form.notSpecified')}</span>
                                )}
                                <Calendar className="ml-2 h-3 w-3 text-slate-500" />
                              </button>
                            </PopoverTrigger>
                            {field.value ? (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  field.onChange("");
                                }}
                                className="inline-flex h-4 w-4 shrink-0 items-center justify-center p-0 leading-none text-slate-500 hover:text-slate-700"
                                aria-label={t('pages.projectDetails:form.clearSelection')}
                                title={t('pages.projectDetails:form.clearSelection')}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            ) : null}
                          </div>
                          <PopoverContent className="w-auto p-4" align="start" side="bottom" sideOffset={8} collisionPadding={16}>
                            <CalendarComponent
                              mode="single"
                              selected={projectDateToLocalDate(field.value)}
                              onSelect={(date) => {
                                field.onChange(date ? format(date, "yyyy-MM-dd") : "");
                              }}
                              initialFocus
                              className="p-4"
                              classNames={{
                                months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                                month: "space-y-4",
                                table: "w-full border-collapse space-y-2",
                                head_row: "flex",
                                head_cell: "text-muted-foreground rounded-md w-12 font-normal !text-base",
                                row: "flex w-full mt-2",
                                cell: "h-12 w-12 text-center text-sm p-0 relative",
                                day: "h-12 w-12 p-0 font-normal aria-selected:opacity-100 !text-lg",
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                      </FormControl>
                    </CornerLabelField>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="endDate"
                render={({ field }) => (
                  <FormItem className="space-y-1">
                    <CornerLabelField label={t('pages.projectDetails:form.endDate')}>
                      <FormControl>
                        <Popover>
                          <div className="textbox-base bg-white flex h-9 min-h-9 items-center gap-1">
                            <PopoverTrigger asChild>
                              <button type="button" className="flex w-full items-center justify-end text-right">
                                {field.value ? (
                                  <span className="text-slate-900">{formatDate(field.value)}</span>
                                ) : (
                                  <span className="text-slate-400">{t('pages.projectDetails:form.notSpecified')}</span>
                                )}
                                <Calendar className="ml-2 h-3 w-3 text-slate-500" />
                              </button>
                            </PopoverTrigger>
                            {field.value ? (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  field.onChange("");
                                }}
                                className="inline-flex h-4 w-4 shrink-0 items-center justify-center p-0 leading-none text-slate-500 hover:text-slate-700"
                                aria-label={t('pages.projectDetails:form.clearSelection')}
                                title={t('pages.projectDetails:form.clearSelection')}
                              >
                                <X className="h-3 w-3" />
                              </button>
                            ) : null}
                          </div>
                          <PopoverContent className="w-auto p-4" align="start" side="bottom" sideOffset={8} collisionPadding={16}>
                            <CalendarComponent
                              mode="single"
                              selected={projectDateToLocalDate(field.value)}
                              onSelect={(date) => {
                                field.onChange(date ? format(date, "yyyy-MM-dd") : "");
                              }}
                              initialFocus
                              className="p-4"
                              classNames={{
                                months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
                                month: "space-y-4",
                                table: "w-full border-collapse space-y-2",
                                head_row: "flex",
                                head_cell: "text-muted-foreground rounded-md w-12 font-normal !text-base",
                                row: "flex w-full mt-2",
                                cell: "h-12 w-12 text-center text-sm p-0 relative",
                                day: "h-12 w-12 p-0 font-normal aria-selected:opacity-100 !text-lg",
                              }}
                            />
                          </PopoverContent>
                        </Popover>
                      </FormControl>
                    </CornerLabelField>
                    <FormMessage className="text-xs" />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="fortschritt"
              render={({ field }) => (
                <FormItem className="space-y-2">
                  <label
                    className="block text-sm font-semibold text-[#505050]"
                    style={{ fontFamily: "'SF Pro Display', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif" }}
                  >
                    {t('pages.projectDetails:form.progress')}
                  </label>
                  <div className="relative w-full h-2 bg-white rounded-lg overflow-hidden border border-slate-200">
                    <div
                      className="absolute top-0 left-0 h-full rounded-lg transition-all duration-300 ease-in-out"
                      style={{ width: `${field.value ?? 0}%`, backgroundColor: '#4A90E2' }}
                    />
                    <div
                      className="absolute top-1/2 transform -translate-y-1/2 w-4 h-4 rounded-full border-2 border-white shadow-lg transition-all duration-300 ease-in-out"
                      style={{ left: `calc(${field.value ?? 0}% - 8px)`, backgroundColor: '#4A90E2' }}
                    />
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={field.value ?? 0}
                      onChange={(e) => field.onChange(parseInt(e.target.value, 10))}
                      className="absolute top-0 left-0 w-full h-full opacity-0 z-10 cursor-pointer"
                    />
                  </div>
                  <div className="flex justify-between text-sm text-slate-600 mt-2">
                    <span>0%</span>
                    <span className="font-medium text-blue-600">{field.value ?? 0}%</span>
                    <span>100%</span>
                  </div>
                  <FormMessage className="text-xs" />
                </FormItem>
              )}
            />
            {/* hidden priority for backend compatibility */}
            <FormField
              control={form.control}
              name="projectPriorityId"
              render={({ field }) => (
                <input type="hidden" value={field.value || defaultPriorityId} onChange={field.onChange} />
              )}
            />
            </div>
          </div>

          {/* Right Column - Project Image and Logo placeholder */}
          <div className="space-y-6">
            <ProjectImageUploader
              ref={imageUploaderRef}
              imageUrl={form.watch('bildUrl')}
              onImageUpdate={(imageUrl) => {
                form.setValue('bildUrl', imageUrl || '');
              }}
              hideLabel={true}
              imageHeight="h-[250px]"
            />
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t('pages.projectDetails:settings.projectLogo')}</label>
              <div className="flex flex-col items-center gap-3">
                <div className="w-24 h-24 flex items-center justify-center relative">
                  <img
                    src="/images/icons/dashboard_round_border.svg"
                    alt=""
                    className="absolute inset-0 w-full h-full"
                    aria-hidden="true"
                  />
                  {logoPreviewUrl ? (
                    <div className="w-20 h-20 rounded-full overflow-hidden relative z-10">
                      <img
                        src={logoPreviewUrl}
                        alt={t('pages.projectDetails:settings.projectLogo')}
                        className="w-full h-full object-cover"
                      />
                      <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="absolute top-0 right-0 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ) : (
                    <div className="w-20 h-20 rounded-full flex items-center justify-center bg-white relative z-10">
                      <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#4A90E2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 17L12 22L22 17" stroke="#4A90E2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M2 12L12 17L22 12" stroke="#4A90E2" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                  )}
                </div>
                <input
                  ref={logoInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoFileSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="pillOutline"
                  size="sm"
                  className="text-xs bg-white hover:bg-white"
                  onClick={() => logoInputRef.current?.click()}
                >
                  {logoPreviewUrl ? t('pages.projectDetails:settings.logos.replace') : t('pages.projectDetails:settings.logos.upload')}
                </Button>
              </div>
            </div>
          </div>
                  </div>
                  </div>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </DialogContent>

      <LogoCropModal
        isOpen={showLogoCropModal}
        onClose={handleLogoCropCancel}
        imageFile={selectedLogoFile}
        onSave={handleLogoCropSave}
      />
    </Dialog>
  );
}
